import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ListingCard } from '../../components';
import TopbarSearchForm from '../TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';

import { queryUserCatalogue } from './ProfilePage.duck';
import { storeSearchParams } from './storeSearchParams';
import css from './CatalogueSection.module.css';

const { UUID } = sdkTypes;

/**
 * The dealer's Katalog on their company profile (P25009-410 BR-3).
 *
 * A workshop can connect its whole stock and advertise none of it; this list
 * is then its stock list. Catalogue rows are ordinary published listings that
 * the `visibility` search filter keeps out of marketplace search, so they are
 * read here with the Marketplace API (ProfilePage.duck.js, queryUserCatalogue)
 * and shown with the same cards as the ads above — they open and can be
 * bought like any listing.
 *
 * Searchable with the same box as the ads (spec A7): size, bolt pattern, car
 * or free text, scoped to this store's catalogue. A 6,000-row catalogue is
 * no use as a flat list.
 *
 * Renders nothing for a dealer with no catalogue.
 */
const CatalogueSection = props => {
  const { listings, nextPage, inProgress, profileUserId, filter = {}, hasAny } = props;
  const config = useConfiguration();
  const dispatch = useDispatch();
  const sectionRef = useRef(null);
  const isFiltered = Object.keys(filter).length > 0;
  // Shown once the store is known to have a catalogue, so a search with no
  // hits keeps the box and the way back.
  const isShown = hasAny || listings.length > 0;

  const load = (page, searchParams) =>
    dispatch(queryUserCatalogue(new UUID(profileUserId), config, page, searchParams));

  /* "Visa katalogen på din profil" links to #katalog, but the section only
   * exists once its read is back — later than the router's own hash jump.
   * The store's ads above it may still be loading too, and they push the
   * section down by thousands of pixels on a big store, so the jump follows
   * the section for a few seconds — and stops the moment the visitor scrolls
   * for themselves. */
  useEffect(() => {
    if (!isShown || typeof window === 'undefined') return undefined;
    if (window.location.hash !== '#katalog' || !sectionRef.current) return undefined;

    let lastTop = null;
    const jump = () => {
      const el = sectionRef.current;
      if (!el) return;
      const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
      if (top !== lastTop) {
        lastTop = top;
        el.scrollIntoView({ block: 'start' });
      }
    };
    const timer = window.setInterval(jump, 250);
    const stop = () => window.clearInterval(timer);
    const giveUp = window.setTimeout(stop, 5000);
    const userEvents = ['wheel', 'touchstart', 'keydown'];
    userEvents.forEach(type => window.addEventListener(type, stop, { passive: true }));
    jump();

    return () => {
      stop();
      window.clearTimeout(giveUp);
      userEvents.forEach(type => window.removeEventListener(type, stop));
    };
  }, [isShown]);

  if (!isShown || !profileUserId) {
    return null;
  }

  return (
    <section id="katalog" ref={sectionRef} className={css.section}>
      <div className={css.head}>
        <h2>Katalog</h2>
        <p className={css.lead}>
          Mer ur butikens lager. Artiklarna annonseras inte på marknadsplatsen, men du kan köpa dem
          här.
        </p>
      </div>

      <div className={css.search}>
        <TopbarSearchForm
          className={css.searchForm}
          appConfig={config}
          onSubmit={values => load(1, storeSearchParams(values, config))}
        />
      </div>

      {listings.length > 0 ? (
        <ul className={css.grid}>
          {listings.map(listing => (
            <li key={listing.id.uuid} className={css.cell}>
              <ListingCard listing={listing} showAuthorInfo={false} showFavoriteIcons={true} />
            </li>
          ))}
        </ul>
      ) : inProgress ? null : (
        <p className={css.empty}>Inga artiklar i katalogen matchar din sökning.</p>
      )}

      <div className={css.actions}>
        {nextPage ? (
          <button
            type="button"
            className={css.more}
            disabled={inProgress}
            onClick={() => load(nextPage, filter)}
          >
            {inProgress ? 'Hämtar…' : 'Visa fler'}
          </button>
        ) : null}
        {isFiltered ? (
          <button
            type="button"
            className={css.clear}
            disabled={inProgress}
            onClick={() => load(1, {})}
          >
            Visa hela katalogen
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default CatalogueSection;
